import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  Cookie, 
  Settings, 
  Eye, 
  RotateCw, 
  ShieldCheck, 
  Info,
  ChevronRight,
  ArrowLeft,
  MousePointer2,
  Lock
} from 'lucide-react';

const CookiePolicy: React.FC = () => {
  return (
    <div className="min-h-screen pt-24 pb-20 bg-white relative overflow-hidden font-sans">
      <div className="absolute top-0 right-0 w-full h-[600px] bg-[radial-gradient(circle_at_70%_0%,rgba(6,182,212,0.05)_0%,transparent_70%)] -z-10" />
      
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col items-start gap-4 mb-12"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] font-bold text-zinc-400 hover:text-cyan-600 transition-all group uppercase tracking-[0.2em]">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            Home
          </Link>
          
          <div className="space-y-2">
            <h1 className="text-3xl md:text-5xl font-black font-display text-cyan-950 tracking-tighter uppercase">
              Cookie <span className="italic text-cyan-600">Policy</span>
            </h1>
            <p className="text-zinc-500 font-medium text-sm leading-relaxed max-w-xl">
              Understanding how we use browser technology to power and personalize your creator experience.
            </p>
          </div>

          <div className="flex items-center gap-4 text-[9px] font-bold uppercase tracking-[0.3em] text-cyan-400 pt-2 border-t border-cyan-600/10 w-full">
            <span>Last Updated: May 19, 2026</span>
            <span className="w-1 h-1 rounded-full bg-cyan-200" />
            <span>Consent ID: FCG-CK-01</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-8">
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 group transition-all"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-600/20 group-hover:rotate-12 transition-transform">
                <Cookie className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight flex items-center gap-3">
                  1. What are Cookies?
                </h2>
                <p className="text-zinc-600 font-medium leading-relaxed text-sm">
                  Cookies are small data files placed on your device to help us recognize you, remember your settings (like physics engine presets), and improve site performance.
                </p>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-cyan-50/20 p-8 md:p-10 rounded-[2rem] border border-cyan-100"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-600/20">
                <Settings className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6 w-full">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">2. How We Use Cookies</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-6 bg-white rounded-3xl border border-cyan-100 space-y-4">
                    <div className="flex items-center gap-3 mb-2 font-black text-[10px] text-cyan-600 uppercase tracking-widest">
                       <Lock className="w-4 h-4" /> Essential Cookies
                    </div>
                    <p className="text-xs font-medium text-zinc-600 leading-relaxed italic">
                      Strictly necessary for security and core functionality, including session management and authentication tokens. These cannot be disabled.
                    </p>
                  </div>
                  <div className="p-6 bg-white rounded-3xl border border-cyan-100 space-y-4">
                    <div className="flex items-center gap-3 mb-2 font-black text-[10px] text-cyan-600 uppercase tracking-widest">
                       <MousePointer2 className="w-4 h-4" /> Experience Cookies
                    </div>
                    <p className="text-xs font-medium text-zinc-600 leading-relaxed italic">
                      Used to store your Studio preferences, such as default render resolution, theme selection, and physics simulation settings.
                    </p>
                  </div>
                  <div className="p-6 bg-white rounded-3xl border border-cyan-100 space-y-4">
                    <div className="flex items-center gap-3 mb-2 font-black text-[10px] text-cyan-600 uppercase tracking-widest">
                       <Eye className="w-4 h-4" /> Analytics Cookies
                    </div>
                    <p className="text-xs font-medium text-zinc-600 leading-relaxed italic">
                      Help us understand aggregate user behavior to optimize render speeds and engine performance using tools like Google Analytics or PostHog.
                    </p>
                  </div>
                  <div className="p-6 bg-white rounded-3xl border border-cyan-100 space-y-4">
                    <div className="flex items-center gap-3 mb-2 font-black text-[10px] text-cyan-600 uppercase tracking-widest">
                       <RotateCw className="w-4 h-4" /> Third-Party Cookies
                    </div>
                    <p className="text-xs font-medium text-zinc-600 leading-relaxed italic">
                      Associated with our payment processors (Stripe/PayPal) and help prevent fraudulent transactions and manage subscriptions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start text-zinc-600 font-medium">
              <div className="w-14 h-14 bg-cyan-50 rounded-2xl flex items-center justify-center shrink-0 border border-cyan-100">
                <ShieldCheck className="w-7 h-7 text-cyan-600" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight italic line-through decoration-cyan-400/20">3. Managing Preferences</h2>
                <div className="space-y-4 text-sm leading-relaxed">
                  <p>You can manage your cookie preferences through our <Link to="/settings" className="text-cyan-600 font-bold hover:underline">Settings Dashboard</Link> or via your browser's security settings. Note that disabling certain cookies may impact the persistence of your simulation projects.</p>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="pt-8"
          >
            <div className="bg-cyan-50/20 p-10 md:p-14 rounded-[3rem] border border-cyan-100 shadow-2xl relative overflow-hidden font-sans text-cyan-950">
              <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px] -z-0" />
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <h2 className="text-2xl md:text-4xl font-heavy font-display uppercase tracking-tight leading-none italic">
                    Developer <br />
                    <span className="text-cyan-600/60">Notice</span>
                  </h2>
                  <p className="text-cyan-800/80 font-medium text-xs leading-relaxed">
                    By continuing to use Creator Studio, you agree to our use of essential cookies as described in this policy.
                  </p>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="p-6 bg-white rounded-3xl group shadow-xl shadow-cyan-900/5 hover:bg-cyan-50 transition-all border border-cyan-100">
                    <p className="text-[10px] font-black uppercase text-cyan-400 tracking-[0.2em] mb-2 font-display italic">Grievance Redressal</p>
                    <p className="text-[10px] font-bold text-cyan-900 leading-relaxed uppercase tracking-wider">
                       Queries specifically regarding tracking and cookies should be directed to <span className="hover:underline transition-all">support@funcirclegames.com</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
